import { memo } from "react";
import { ExternalLink } from "lucide-react";
import { FACEBOOK_FEED_ASPECT_CLASS, MediaPreview } from "@nexus/ui";
import type { ExampleCardVm } from "./ExampleCreativeGrid.js";

interface ExampleCreativeCardProps {
  card: ExampleCardVm;
}

function ExampleCreativeCardImpl({ card }: ExampleCreativeCardProps) {
  return (
    <div className="group relative w-full overflow-hidden rounded-md border bg-card shadow-[0_1px_3px_0_rgba(0,0,0,0.1),0_1px_2px_-1px_rgba(0,0,0,0.1)] transition-shadow duration-150 [content-visibility:auto] [contain-intrinsic-size:auto_360px] hover:shadow-[0_4px_8px_-2px_rgba(0,0,0,0.1),0_2px_4px_-2px_rgba(0,0,0,0.1)]">
      {card.accentColor ? (
        <span
          aria-hidden="true"
          className="pointer-events-none absolute inset-y-0 left-0 z-10 w-[3px]"
          style={{ backgroundColor: card.accentColor }}
        />
      ) : null}

      <MediaPreview
        src={card.src || undefined}
        thumbnailSrc={card.thumbnailUrl}
        type={card.type}
        alt={card.title}
        title={card.title}
        meta={card.meta ? <span>{card.meta}</span> : undefined}
        className={`${FACEBOOK_FEED_ASPECT_CLASS} overflow-hidden rounded-t-md bg-muted`}
      >
        {card.type === "video" ? (
          <div className="pointer-events-none absolute inset-0 flex items-center justify-center transition-opacity duration-200 group-hover:opacity-0">
            <span className="flex size-10 items-center justify-center rounded-full bg-black/40 text-white ring-1 ring-white/25 backdrop-blur-[2px]">
              <svg viewBox="0 0 24 24" fill="currentColor" className="size-5" aria-hidden="true">
                <path d="M8 5v14l11-7z" />
              </svg>
            </span>
          </div>
        ) : null}
      </MediaPreview>

      <div className="px-2.5 pb-1 pt-2">
        <div className="flex items-center gap-1.5">
          <p className="min-w-0 flex-1 truncate text-[13px] font-medium leading-tight text-foreground">
            {card.title}
          </p>
          {card.subtitleHref ? (
            <a
              href={card.subtitleHref}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex shrink-0 items-center gap-1 text-[11px] text-primary hover:underline"
            >
              <span>{card.subtitle}</span>
              <ExternalLink className="size-3 shrink-0" aria-hidden="true" />
            </a>
          ) : null}
        </div>
        {card.subtitle && !card.subtitleHref ? (
          <p className="truncate text-[11px] text-muted-foreground">{card.subtitle}</p>
        ) : null}
      </div>

      <div className="flex flex-col gap-1 px-2.5 pb-2.5 pt-1">
        {card.metrics.map((metric) => (
          <div
            key={metric.label}
            className="flex items-center justify-between gap-2 rounded-lg border border-border bg-muted/30 px-2 py-1.5"
          >
            <span className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
              {metric.label}
            </span>
            <span className="truncate text-xs font-medium tabular-nums text-foreground">{metric.value}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// Memoized so the virtualizer's per-scroll re-renders only touch cards entering
// or leaving the window — card view models are recomputed per render but shallow,
// and stable keys keep React from remounting off-screen cards.
export const ExampleCreativeCard = memo(ExampleCreativeCardImpl);
